/**
 * AdminLeaderboard.jsx
 * 
 * Admin dashboard for viewing house competition rankings and managing students.
 * Features:
 * - Real-time leaderboard with auto-refresh every 5 seconds
 * - House point visualization with weighted scoring for balance
 * - Inline student bonus point editing and removal
 * - QR code display for easy student access
 * - Admin panel navigation
 */

import { useEffect, useMemo, useState, useRef } from 'react';
import { getCurrentUser } from '../App.jsx';
import { confirm, alertSuccess, alertError } from '../utils/alerts';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

// Constants
const HOUSES = ['Gryffindor', 'Hufflepuff', 'Ravenclaw', 'Slytherin'];
const HOUSE_COLORS = {
  'Gryffindor': '#740001',    // Scarlet red
  'Hufflepuff': '#ECBA1A',    // Bright yellow
  'Ravenclaw': '#2E5C8A',     // Medium blue (lightened from deep blue)
  'Slytherin': '#1A472A'      // Dark green
};
const AUTO_REFRESH_INTERVAL_MS = 5000;

export default function AdminLeaderboard() {
  // State for data management
  const [students, setStudents] = useState([]);
  const [currentUser, setCurrentUser] = useState(getCurrentUser());
  const [submissions, setSubmissions] = useState([]);
  const [schoolCreatorId, setSchoolCreatorId] = useState(null);

  // State for UI interactions
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [editingBonusPoints, setEditingBonusPoints] = useState('');

  // Refs and navigation
  const bonusPointsInputRef = useRef();
  const navigate = useNavigate();

  /**
   * Fetch all necessary data from the server
   * Called on mount and every 5 seconds for real-time updates
   */
  const fetchData = async () => {
    const user = getCurrentUser();
    setCurrentUser(user);

    if (!user?.code) {
      setStudents([]);
      return;
    }

    try {
      // Fetch students data
      const studentsResponse = await fetch(`/api/students/${user.code}`);
      const studentsData = await studentsResponse.json();
      setStudents(Array.isArray(studentsData) ? studentsData : []);

      // Fetch school information to get creator ID
      const schoolResponse = await fetch(`/api/school/${user.code}`);
      const schoolData = await schoolResponse.json();
      setSchoolCreatorId(schoolData?.creatorId || null);

      // Fetch quiz submissions for scoring
      const submissionsResponse = await fetch(`/api/submissions/${currentUser.code}`);
      const submissionsData = await submissionsResponse.json();
      setSubmissions(Array.isArray(submissionsData) ? submissionsData : []);

    } catch (error) {
      console.error('Failed to fetch leaderboard data:', error);
      // Set fallback empty states on error
      setStudents([]);
      setSubmissions([]);
      setSchoolCreatorId(null);
    }
  };

  // Set up data fetching on component mount and auto-refresh
  useEffect(() => {
    fetchData();
    const refreshInterval = setInterval(fetchData, AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(refreshInterval);
  }, []);
  // Aggregate by house and compute per-player contributions
  // Do not include Unsorted in the main bar chart; we'll render unsorted players below
  const houses = useMemo(() => ['Gryffindor', 'Hufflepuff', 'Ravenclaw', 'Slytherin'], []);
  const houseData = useMemo(() => {
    const byHouse = {};
    for (const h of houses) byHouse[h] = { total: 0, players: [] };

    for (const st of students || []) {
      // normalize house: trim and title-case (e.g., 'gryffindor' -> 'Gryffindor')
      const raw = (st.house || '').toString().trim();
      const house = raw ? (raw[0].toUpperCase() + raw.slice(1).toLowerCase()) : 'Unsorted';
      
      // Use the points directly from the students table (includes quiz, activity, and scramble points)
      // Then add any bonus points awarded by admin
      const studentPts = Number(st.points) || 0;
      const bonusPts = Number(st.bonusPoints) || 0;
      const points = studentPts + bonusPts;
      
      if (!byHouse[house]) byHouse[house] = { total: 0, players: [] };
      byHouse[house].total += points;
      byHouse[house].players.push({ id: st.id, name: st.name, points });
    }
    // sort players in each house by points descending
    for (const h of Object.keys(byHouse)) {
      byHouse[h].players.sort((a, b) => (b.points || 0) - (a.points || 0));
    }
    // Apply weighting based on house sizes so smaller houses receive a stronger boost
    // Weight formula: weight = maxHouseSize / thisHouseSize (linear)
    const sizes = Object.fromEntries(Object.entries(byHouse).map(([k, v]) => [k, v.players.length || 0]));
    const maxSize = Math.max(1, ...Object.values(sizes));
    for (const h of Object.keys(byHouse)) {
      const size = sizes[h] || 0; // if no players, weighted total remains 0
      const weight = size > 0 ? (maxSize / size) : 0;
      // compute weighted totals and per-player weighted points (but show raw in UI list)
      byHouse[h].weightedTotal = Math.round((byHouse[h].total || 0) * weight);
      byHouse[h].players = byHouse[h].players.map(p => ({ ...p, weightedPoints: Math.round((p.points || 0) * weight) }));
    }
    return byHouse;
  }, [students, houses, submissions]);

  const maxPoints = useMemo(() => {
    const vals = Object.values(houseData).map(h => h.weightedTotal || h.total || 0);
    return Math.max(1, ...vals);
  }, [houseData]);

  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const editRef = useRef();

  // Side tab open state for hover reveal
  const [sideTabOpen, setSideTabOpen] = useState(false);
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Hover tab: fixed on the left side, reveals icons when hovered */}
      <div
        onMouseEnter={() => setSideTabOpen(true)}
        onMouseLeave={() => setSideTabOpen(false)}
        style={{
          position: 'fixed',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          // Always align items from flex-start to prevent shifting
          justifyContent: 'flex-start',
          background: '#0f172a',
          border: '1px solid #334155',
          borderRadius: '0 8px 8px 0',
          padding: 4,
          // closed width must fit the handle; open width just large enough for the QR icon
          width: sideTabOpen ? 88 : 44,
          transition: 'width 180ms ease',
          boxShadow: '0 6px 18px rgba(2,6,23,0.6)'
        }}
        aria-hidden={false}
      >
        {/* Visible handle (always) - acts as Admin Panel button */}
        <button
          aria-label="Admin Panel"
          title="Admin Panel"
          onClick={() => navigate('/admin/school')}
          style={{ 
            width: 45, 
            height: 36, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flexShrink: 0, 
            background: 'transparent', 
            border: 'none', 
            cursor: 'pointer',
            // Center the button within the container when closed
            marginLeft: sideTabOpen ? 0 : 'auto',
            marginRight: sideTabOpen ? 0 : 'auto'
          }}
        >
          <img src="/icons/settings.png" alt="Admin" style={{ width: 20, height: 20, filter: 'brightness(0) invert(1)' }} />
        </button>

        {/* Sliding icons area */}
        <div style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          marginLeft: 6,
          // hide slightly off-canvas when closed, move fully visible when open
          transform: sideTabOpen ? 'translateX(0)' : 'translateX(-22px)',
          opacity: sideTabOpen ? 1 : 0,
          transition: 'transform 200ms ease, opacity 160ms ease'
        }}>
          <button onClick={() => {
            Swal.fire({
              title: `<div style="font-family: 'Cinzel Decorative', serif; font-size:20px; letter-spacing:1px;">SCAN TO JOIN</div>`,
              html: `<div style=\"display:flex;flex-direction:column;align-items:center;gap:12px;\"><img src=\"/images/qr.png\" style=\"width:380px;height:auto;\" /><div style=\"font-family: 'Cinzel Decorative', serif; color:#e2e8f0; margin-top:6px;\"></div></div>`,
              background: '#0f172a',
              color: '#e2e8f0',
              showCloseButton: true,
              showConfirmButton: false,
            });
          }} style={{ ...iconBtn }} title="Show QR Code">
            <img src="/icons/qr.png" alt="QR Code" style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }} />
          </button>
        </div>
      </div>
      <h1 style={{ 
        fontFamily: "'Cinzel Decorative', serif", 
        textAlign: 'center', 
        borderBottom: 'none', 
        textDecoration: 'none',
        border: 'none',
        outline: 'none',
        margin: '16px 0',
        padding: '0'
      }}>House Points Leaderboard</h1>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, alignItems: 'center', border: 'none', borderTop: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Settings icon button using new icon */}

          <div style={{ textAlign: 'center', fontStyle: 'italic', opacity: 0.8 }}>
            Game Code: <strong>{currentUser?.code}</strong>
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
              Students: { (students || []).filter(s => s.house && s.house.toString().trim() !== '').length } • Quiz submissions: {(submissions || []).length}
            </div>
          </div>
        </div>

      </div>

      
      <div style={{ display: 'grid', gap: 16 }}>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', justifyContent: 'center' }}>
          {houses.map((h, idx) => {
            const hd = houseData[h] || { total: 0, players: [] };
            const color = HOUSE_COLORS[h] || '#4f46e5';
            return (
              <div key={h} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 140 }}>
                {/* Fixed top block so the title + bar + total occupy consistent vertical space */}
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: 280 }}>
                  <div style={{ marginBottom: 8, textAlign: 'center' }}>
                    <strong>{h}</strong>
                  </div>

                  {/* vertical bar container */}
                  <div style={{ height: 220, width: 72, background: '#0f172a', borderRadius: 8, overflow: 'hidden', border: '1px solid #334155', display: 'flex', alignItems: 'flex-end' }}>
                    <div
                      style={{
                        height: `${Math.round(((hd.weightedTotal || hd.total) / maxPoints) * 100)}%`,
                        width: '100%',
                        background: color,
                        transition: 'height 400ms ease',
                      }}
                    />
                  </div>
                  <div style={{ marginTop: 8, textAlign: 'center', opacity: 0.85 }}>{hd.weightedTotal ?? hd.total} pts</div>
                </div>

                {/* players listed under the bar (grows down the page) */}
                <div style={{ display: 'grid', gap: 6, marginTop: 12, width: '100%' }}>
                  {hd.players.length > 0 ? (
                    hd.players.map(p => (
                      <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span
                              onClick={() => { 
                                setEditingId(editingId === p.id ? null : p.id); 
                                // Find the student to get their bonus points
                                const student = students.find(s => s.id === p.id);
                                setEditValue(student?.bonusPoints || 0); 
                              }}
                              style={{ opacity: 0.95, cursor: 'pointer', textDecoration: 'underline' }}
                            >{p.name}</span>
                          </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={{ opacity: 0.8 }}>{p.points} pts</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ opacity: 0.6, fontSize: 13 }}>No players</div>
                  )}
                </div>
                {/* inline editor */}
                {editingId && hd.players.find(x => x.id === editingId) && (
                  <div style={{ marginTop: 8, width: '100%', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <input ref={editRef} type="number" value={editValue} onChange={e => setEditValue(Number(e.target.value))} style={{ width: 80, padding: 4, borderRadius: 6, fontSize: 13, textAlign: 'right' }} />
                    <button onClick={async () => {
                      const studentId = editingId;
                      const newBonusPoints = Number(editValue) || 0;
                      try {
                        await fetch(`/api/students/${currentUser.code}/patch`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: studentId, bonusPoints: newBonusPoints }) });
                        // refresh students
                        const res = await fetch(`/api/students/${currentUser.code}`);
                        const s = await res.json();
                        setStudents(Array.isArray(s) ? s : []);
                        // update local hp_user if this was the current user
                        try {
                          const raw = localStorage.getItem('hp_user');
                          if (raw) {
                            const stored = JSON.parse(raw);
                            if (stored.id === studentId) {
                              stored.bonusPoints = newBonusPoints;
                              localStorage.setItem('hp_user', JSON.stringify(stored));
                              setCurrentUser(stored);
                            }
                          }
                        } catch (e) {}
                        setEditingId(null);
                      } catch (err) {
                        console.error('Failed to patch student', err);
                      }
                    }} style={{ padding: '6px 8px', borderRadius: 6 }}>Save</button>
                    <button onClick={async () => {
                      const studentId = editingId;
                      try {
                        await fetch(`/api/students/${currentUser.code}/${studentId}`, { method: 'DELETE' });
                        const res = await fetch(`/api/students/${currentUser.code}`);
                        const s = await res.json();
                        setStudents(Array.isArray(s) ? s : []);
                        setEditingId(null);
                      } catch (err) { console.error('Failed to delete student', err); }
                    }} style={{ padding: '6px 8px', borderRadius: 6, background: '#ef4444', color: '#fff' }}>Remove</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {students.length === 0 && (
          <div style={{ opacity: 0.7 }}>

          </div>
        )}
      </div>

      {/* Awaiting House Sorting list (unsorted players) - exclude current admin and hide when empty */}
      {(() => {
        const unsorted = (students || []).filter(s => !s.house || s.house.toString().trim() === '');
        // Exclude the school's creator from the awaiting list (do not show the creator)
        // Also exclude students marked as hiddenFromSorting
        const filtered = unsorted.filter(s => s.id !== schoolCreatorId && !s.hiddenFromSorting);
        if (filtered.length === 0) return null;
        
        const handleNameClick = async (student) => {
          const result = await confirm(
            'Hide from Sorting List?',
            `Do you want to hide ${student.name} from the awaiting house sorting list? Their account will remain active.`
          );
          
          if (result.isConfirmed) {
            try {
              const payload = { id: student.id, hiddenFromSorting: 1 };
              console.log('Sending payload:', payload);
              
              const response = await fetch(`/api/students/${currentUser.code}/patch`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              });
              
              if (!response.ok) {
                const errorData = await response.json();
                console.error('Server error:', errorData);
                throw new Error(errorData.error || 'Failed to update student');
              }
              
              await fetchData(); // Refresh the list
              await alertSuccess(
                'Hidden!',
                `${student.name} has been hidden from the sorting list.`
              );
            } catch (err) {
              console.error('Failed to hide student', err);
              await alertError(
                'Error',
                'Failed to hide student from sorting list.'
              );
            }
          }
        };
        
        return (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <h2 style={{ fontFamily: "'Cinzel Decorative', serif", marginBottom: 8 }}>Awaiting House Sorting</h2>
            <div style={{ display: 'grid', gap: 6, justifyContent: 'center' }}>
              {filtered.map(s => (
                <div 
                  key={s.id} 
                  onClick={() => handleNameClick(s)}
                  style={{ 
                    fontSize: 14, 
                    opacity: 0.9,
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: 4,
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  {s.name}
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const secondaryBtn = {
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #475569',
  background: 'transparent',
  color: '#a0b4ff',
  cursor: 'pointer',
};

const iconBtn = {
  padding: 8,
  borderRadius: 8,
  border: '1px solid transparent',
  background: 'transparent',
  color: '#a0b4ff',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};